// Голосовой композер (SPEC §4.2 «Голос», FR-VOICE). Делегирует запись voice-recorder
// (createVoiceRecorder): жесты InputBar.onMicPress* → recorder.pressStart/End/Cancel 1:1
// (увести-отменить НЕ отправляет — инв.2 рекордера). onRecordingSignal → typing{recording_voice}
// через стор. Загрузка аудио — MediaUploadPort поверх transport.rest. onSend/onTranscript —
// информативны (стор сам сводит оптимистику и догоняет расшифровку из transport.events()).

import { useEffect, useRef, useState } from 'react'
import type { ChatId } from '@tele-chat/shared-contracts'
import {
  createVoiceRecorder,
  createBrowserCapture,
  type VoiceRecorder,
  type RecorderSnapshot,
  type TransportPort as VoiceTransportPort,
} from '@tele-chat/voice-recorder'
import { useStore } from '../store/StoreContext.js'
import { useTransport } from '../transport/TransportContext.js'
import { createMediaUploadPort } from '../media/upload.js'

export interface VoiceComposerApi {
  /** Снимок состояния записи (для InputBar.recording / прогресс). */
  snapshot: RecorderSnapshot
  pressStart(): void
  pressEnd(): void
  pressCancel(): void
  toggleLock(): void
}

/**
 * Подключает рекордер к чату. Пересоздаётся при смене chatId (рекордер привязан к chatId). На
 * размонтировании — dispose (освобождает треки/таймеры, без отправки).
 */
export function useVoiceComposer(chatId: ChatId): VoiceComposerApi {
  const store = useStore()
  const transport = useTransport()
  const recorderRef = useRef<VoiceRecorder | null>(null)
  const [snapshot, setSnapshot] = useState<RecorderSnapshot>({
    state: 'idle',
    elapsedMs: 0,
    liveWaveform: [],
    locked: false,
  })

  useEffect(() => {
    // Транспорт client-transport структурно совместим с VoiceTransportPort (newClientMsgId/outbox/
    // rest/events) — отдаём его как есть (через приведение поверхностей).
    const voiceTransport = transport as unknown as VoiceTransportPort
    const recorder = createVoiceRecorder({
      chatId,
      transport: voiceTransport,
      mediaUpload: createMediaUploadPort(transport.rest, chatId),
      capture: createBrowserCapture(),
      onRecordingSignal: (on: boolean) => {
        if (on) store.actions.notifyTyping(chatId, 'recording_voice')
      },
      // onSend/onTranscript — стор сводит сам; явных действий не требуется.
    })
    recorderRef.current = recorder
    const unsub = recorder.subscribe(setSnapshot)
    return () => {
      unsub()
      recorder.dispose()
      recorderRef.current = null
    }
  }, [chatId, transport, store])

  return {
    snapshot,
    pressStart: () => void recorderRef.current?.pressStart(),
    pressEnd: () => void recorderRef.current?.pressEnd(),
    pressCancel: () => recorderRef.current?.pressCancel(),
    toggleLock: () => recorderRef.current?.toggleLock(),
  }
}
